/**
 * Website Compliance Checker - Compliance Result Types
 *
 * These types define the structure of compliance check results,
 * findings, and reports.
 */

import type { BusinessContext } from './business-category';
import type { RuleCategory, RuleRequirement, RuleSeverity } from './compliance-rule';

/**
 * Status of an individual compliance check.
 */
export type CheckStatus = 'PASS' | 'FAIL' | 'MANUAL_REVIEW' | 'NOT_APPLICABLE' | 'PENDING' | 'ERROR';

/**
 * Overall status of a compliance report.
 */
export type ComplianceOverallResult = 'COMPLIANT' | 'NON_COMPLIANT' | 'PARTIALLY_COMPLIANT';

/**
 * Evidence supporting a compliance finding.
 * Provides context for where and what was found.
 */
export interface ComplianceEvidence {
    /** URL of the page where evidence was found */
    url?: string;

    /** Text snippet showing the evidence (max 200 chars) */
    snippet?: string;

    /** Extracted value (e.g., ABN number, AFSL number) */
    extractedValue?: string;

    /** Location on the page (e.g., footer, header, main) */
    location?: 'header' | 'footer' | 'main' | 'sidebar' | 'popup' | 'modal' | 'document';

    /** Additional validation details */
    validationDetails?: Record<string, unknown>;
}

/**
 * Individual compliance check result.
 * Each rule produces one finding.
 */
export interface ComplianceFinding {
    /** ID of the rule that was checked */
    ruleId: string;

    /** Human-readable name of the rule */
    ruleName: string;

    /** Category for grouping */
    category: RuleCategory;

    /** Result status */
    status: CheckStatus;

    /** Severity if non-compliant */
    severity: RuleSeverity;

    /** Whether this is a must-have or nice-to-have */
    requirement: RuleRequirement;

    /** Evidence of compliance or non-compliance */
    evidence?: ComplianceEvidence;

    /** AI-generated explanation of the finding */
    explanation: string;

    /** Recommendation for fixing (if non-compliant) */
    recommendation?: string;

    /** Confidence score (0-1) */
    confidence: number;

    /** Whether manual review is recommended */
    requiresManualReview?: boolean;

    /** Reasons for manual review */
    manualReviewReasons?: string[];
}

/**
 * ABN validation result from ABR API.
 */
export interface ABNValidationResult {
    /** The ABN number */
    abn: string;

    /** Whether the ABN is valid and current */
    isValid: boolean;

    /** Status from ABR (Active/Cancelled) */
    abnStatus: 'Active' | 'Cancelled' | 'Unknown';

    /** Registered entity name */
    entityName: string;

    /** Entity type (e.g., Company, Trust) */
    entityType: string;

    /** Whether GST registered */
    gstRegistered: boolean;

    /** Registered business names */
    businessNames: string[];

    /** Whether the website name matches ABN name */
    nameMatches: boolean;

    /** Whether "trading as" statement is present if names don't match */
    tradingAsPresent?: boolean;

    /** Validation timestamp */
    validatedAt: Date;
}

/**
 * AFSL extraction result (manual review required).
 */
export interface AFSLExtractionResult {
    /** The AFSL/AFS Licence number */
    licenceNumber: string;

    /** Name associated with the licence (if found) */
    licenceName?: string;

    /** Direct link to ASIC register for manual verification */
    asicLink: string;

    /** Whether manual verification is required */
    requiresManualVerification: boolean;

    /** Notes about what to verify */
    verificationNotes?: string[];

    /** Extraction timestamp */
    extractedAt: Date;
}

/**
 * Progress tracking for compliance analysis.
 */
export interface ComplianceAnalysisProgress {
    /** Current step in the analysis */
    currentStep:
        | 'QUEUED'
        | 'SCRAPING'
        | 'PREPROCESSING'
        | 'EXTRACTING'
        | 'VALIDATING'
        | 'ANALYZING'
        | 'FINALIZING'
        | 'COMPLETED'
        | 'ERROR';

    /** Number of pages scraped */
    pagesScraped?: number;

    /** Number of pages processed */
    pagesProcessed?: number;

    /** Number of rules checked */
    rulesChecked?: number;

    /** Total rules to check */
    totalRules?: number;

    /** Start time */
    startTime?: Date;

    /** Estimated time remaining (seconds) */
    estimatedTimeRemaining?: number;

    /** Last update time */
    lastUpdated?: Date;

    /** Number of pages that have completed deduplication */
    dedupPagesProcessed?: number;

    /** Total number of pages to deduplicate */
    dedupPagesTotal?: number;
}

/**
 * Human-readable labels for analysis steps.
 */
export const ANALYSIS_STEP_LABELS: Record<ComplianceAnalysisProgress['currentStep'], string> = {
    QUEUED: 'Queued',
    SCRAPING: 'Scanning pages',
    PREPROCESSING: 'Deduplicating content',
    EXTRACTING: 'Extracting document links',
    VALIDATING: 'Classifying pages',
    ANALYZING: 'Checking compliance rules',
    FINALIZING: 'Generating report',
    COMPLETED: 'Completed',
    ERROR: 'Error',
};

/**
 * Full compliance report.
 * Contains all findings and aggregated results.
 * Note: Status and progress are managed by ComplianceScan, not the report itself.
 */
export interface ComplianceReport {
    /** Report ID */
    id: bigint;

    /** Organisation ID */
    orgId: bigint;

    /** Associated scraped data ID */
    scrapedDataId: bigint;

    /** Website URL that was checked */
    websiteUrl: string;

    /** Business context from questionnaire */
    businessContext: BusinessContext;

    /** Overall compliance result */
    overallResult?: ComplianceOverallResult;

    /** Compliance score (0-100) */
    complianceScore?: number;

    /** Total number of checks performed */
    totalChecks: number;

    /** Number of passed checks */
    passCount: number;

    /** Number of failed checks */
    failCount: number;

    /** Number of checks requiring manual review */
    manualReviewCount: number;

    /** All compliance findings */
    findings: ComplianceFinding[];

    /** Executive summary */
    summary?: string;

    /** Key compliance strengths */
    keyStrengths?: string[];

    /** Critical compliance gaps */
    criticalGaps?: string[];

    /** ABN validation result (if performed) */
    abnValidation?: ABNValidationResult;

    /** AFSL extraction result (if performed) */
    afslExtraction?: AFSLExtractionResult;

    /** Error message (if failed) */
    errorMessage?: string;

    /** Analysis version for tracking */
    analysisVersion: string;

    /** When the report was created */
    createdAt: Date;

    /** When analysis was completed */
    analyzedAt?: Date;
}

/**
 * Summary view of a compliance report for list displays.
 * Note: Status is managed by ComplianceScan, not the report itself.
 */
export interface ComplianceReportSummary {
    /** Report ID */
    id: bigint;

    /** Website URL */
    websiteUrl: string;

    /** Business category */
    businessCategory: string;

    /** Overall result */
    overallResult?: ComplianceOverallResult;

    /** Compliance score */
    complianceScore?: number;

    /** Critical issues count */
    criticalIssuesCount: number;

    /** When created */
    createdAt: Date;

    /** When analyzed */
    analyzedAt?: Date;
}

/**
 * Display labels for check status
 */
export const CHECK_STATUS_LABELS: Record<CheckStatus, string> = {
    PASS: 'Pass',
    FAIL: 'Fail',
    MANUAL_REVIEW: 'Manual Review',
    NOT_APPLICABLE: 'Not Applicable',
    PENDING: 'Pending',
    ERROR: 'Error',
};

/**
 * Display labels for overall result
 */
export const OVERALL_RESULT_LABELS: Record<ComplianceOverallResult, string> = {
    COMPLIANT: 'Compliant',
    NON_COMPLIANT: 'Non-Compliant',
    PARTIALLY_COMPLIANT: 'Partially Compliant',
};
